import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { clearToken, getToken, setToken } from "../Services/authStore";
import { login, register, me, type MeResponse, type Role, type AuthInput, type RegisterInput } from "../Services/AuthService";

const roleRank: Record<Role, number> = {
  USER: 1,
  CONTRIBUTOR: 2,
  ADMIN: 3,
  SUPERADMIN: 4,
};

type AuthCtx = {
  user: MeResponse | null;
  role: Role | null;
  isLoading: boolean;
  isAuthenticated: boolean;

  hasRole: (...roles: Role[]) => boolean;
  hasAtLeast: (min: Role) => boolean;

  refresh: () => Promise<void>;
  login: (data: AuthInput) => Promise<void>;
  register: (data: RegisterInput) => Promise<void>;
  logout: () => void;
};

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<MeResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      return;
    }

    try {
      const u = await me();
      setUser(u);
    } catch {
      // fail closed
      clearToken();
      setUser(null);
    }
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await refresh();
      setIsLoading(false);
    })();

  }, []);

  const doLogin = async (data: AuthInput) => {
    setIsLoading(true);
    try {
      const res = await login(data);
      setToken(res.token);
      await refresh();
    } finally {
      setIsLoading(false);
    }
  };

  const doRegister = async (data: RegisterInput) => {
    setIsLoading(true);
    try {
      const res = await register(data);
      setToken(res.token);
      await refresh();
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    clearToken();
    setUser(null);
  };

  const value = useMemo<AuthCtx>(() => {
    const role = user?.role ?? null;

    return {
      user,
      role,
      isLoading,
      isAuthenticated: !!user && !isLoading,

      hasRole: (...roles) => !!role && roles.includes(role),
      hasAtLeast: (min) => !!role && roleRank[role] >= roleRank[min],

      refresh,
      login: doLogin,
      register: doRegister,
      logout,
    };
  }, [user, isLoading]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
